import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { DollarOutlined, DollarTwoTone } from '@ant-design/icons';
import { Button as AntButton, Spin, Tooltip } from 'antd';
import { handleHideFinancials } from '../services/financials.services';
import { CREATE_ROLES } from '../pages/users-managment/queries';
import { useMutation } from '@apollo/client';
import { Button } from './Button';
import { useUserContext } from '../context/PermissionsContext';

type PageHeaderProps = {
  title: string;
  subtitle?: string;
  description?: string;
  statusText?: string;
  actionButton?: React.ReactNode;
  showDescriptionSection?: boolean;
};

const PageHeader = (props: PageHeaderProps) => {
  const {
    title,
    subtitle,
    description,
    statusText,
    actionButton,
    showDescriptionSection = true,
  } = props;
  const { logout } = useAuth0();

  const { isAdmin, canViewFinancials, currentRoles } = useUserContext();

  const FinancialsIcon = canViewFinancials ? DollarTwoTone : DollarOutlined;

  const [updateFinancials, { loading: assignPermissionsLoading }] = useMutation(
    CREATE_ROLES,
    {
      refetchQueries: ['GetAllUsers', 'GetUserPermissions'],
    },
  );

  return (
    <section className="mb-6">
      <div className="flex flex-row flex-wrap items-center content-center justify-between">
        <h1 className="text-primary-100 font-bold text-3xl tracking-widest">
          {title}
        </h1>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Tooltip
              title={`Toggle Financials ${canViewFinancials ? 'Off' : 'On'}`}
            >
              {!assignPermissionsLoading ? (
                <AntButton
                  icon={<FinancialsIcon />}
                  type="text"
                  shape="circle"
                  onClick={() =>
                    handleHideFinancials(currentRoles, updateFinancials)
                  }
                />
              ) : (
                <Spin spinning size="default" />
              )}
            </Tooltip>
          )}
          <Button
            type="logout"
            className="text-sm"
            onClick={() =>
              logout({
                logoutParams: {
                  returnTo: window.location.origin,
                },
              })
            }
          >
            Logout
          </Button>
        </div>
      </div>
      {showDescriptionSection && (
        <div className="w-full flex justify-between items-end">
          <div className="flex-1">
            <h2 className="mt-3 text-primary-100 font-normal text-lg tracking-widest">
              {subtitle}
            </h2>
            {description && (
              <div className="mt-8 text-primary-100 font-normal">
                {description}
              </div>
            )}
          </div>
          {(actionButton || statusText) && (
            <div className="flex items-center ml-12 gap-4">
              {statusText && (
                <span className="text-sm italic text-gray-600 whitespace-nowrap">
                  {statusText}
                </span>
              )}
              {actionButton}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default PageHeader;
